import React, { useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const PAGES = [
  {
    title: "How to Play",
    body: (
      <div>
        <p className="text-gray-700 text-sm leading-relaxed mb-3">
          Come up with words in the same category, all starting with the
          round's letter.
        </p>
        <ol className="space-y-1.5">
          {[
            "Random letter each round.",
            "45s to fill Name, Animal, Place, Thing.",
            "Answers must start with that letter.",
            "Time's up or everyone's in — review begins.",
            "Players vote on answers (or AI reviews automatically, if the host turned that on).",
            "Valid, unique answers score.",
          ].map((step, i) => (
            <li key={i} className="flex gap-2 text-gray-700 text-sm">
              <span className="font-bold text-red-600">{i + 1}.</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      </div>
    ),
  },
  {
    title: "Scoring",
    body: (
      <div>
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="rounded-lg border border-green-200 bg-green-50 py-3 text-center">
            <p className="text-2xl font-bold text-green-700">10</p>
            <p className="text-[10px] text-gray-600 mt-0.5">Unique</p>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 py-3 text-center">
            <p className="text-2xl font-bold text-amber-700">5</p>
            <p className="text-[10px] text-gray-600 mt-0.5">Shared</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 py-3 text-center">
            <p className="text-2xl font-bold text-gray-500">0</p>
            <p className="text-[10px] text-gray-600 mt-0.5">Invalid/blank</p>
          </div>
        </div>
        <p className="text-gray-700 text-sm mb-1.5">A valid answer:</p>
        <ul className="text-gray-700 text-sm space-y-1">
          <li>• Starts with the round's letter</li>
          <li>• Fits the category</li>
          <li>• Is a real word or name</li>
        </ul>
        <p className="text-gray-700 text-sm mt-3">
          <span className="font-semibold">Solo:</span> a valid answer scores up to 15. Rarer
          answers score more, and the same answer as the bot scores half.
        </p>
      </div>
    ),
  },
  {
    title: "How It Works",
    body: (
      <div className="flex flex-col items-center">
        <div className="bg-red-100 text-red-800 text-3xl font-bold rounded-full w-16 h-16 flex items-center justify-center mb-4">
          S
        </div>
        <div className="grid grid-cols-2 gap-2.5 w-full text-sm">
          <div className="bg-blue-50 p-2.5 rounded-xl">
            <span className="text-xs text-gray-400">Name</span>
            <p className="font-semibold text-gray-800 text-xs"> Simi</p>
          </div>
          <div className="bg-green-50 p-2.5 rounded-xl">
            <span className="text-xs text-gray-400">Animal</span>
            <p className="font-semibold text-gray-800 text-xs"> Shark</p>
          </div>
          <div className="bg-yellow-50 p-2.5 rounded-xl">
            <span className="text-xs text-gray-400">Place</span>
            <p className="font-semibold text-gray-800 text-xs"> Seattle</p>
          </div>
          <div className="bg-purple-50 p-2.5 rounded-xl">
            <span className="text-xs text-gray-400">Thing</span>
            <p className="font-semibold text-gray-800 text-xs"> Shoes</p>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-3 text-center">
          Use classics or create your own wild categories!
        </p>
      </div>
    ),
  },
  {
    title: "Twists & Winning",
    body: (
      <div className="space-y-3">
        <p className="text-gray-700 text-sm leading-relaxed">
          <strong>Special Rounds</strong> (if the host turns them on) add a
          surprise twist to scoring — you'll find out when it happens.
        </p>
        <p className="text-gray-700 text-sm leading-relaxed">
          <strong>Speed Mode</strong> kicks in on the final 6 letters: 20s per
          round instead of 45s.
        </p>
        <p className="text-gray-700 text-sm leading-relaxed">
          <strong>Winning:</strong> most points after all letters are used —
          or whenever the host ends the game.
        </p>
      </div>
    ),
  },
];

const GameRulesModal = ({ isOpen, onClose }) => {
  const [page, setPage] = useState(0);

  if (!isOpen) return null;

  const isLast = page === PAGES.length - 1;

  const handleClose = () => {
    setPage(0);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full flex flex-col">
        <div className="p-5 pb-3 flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg font-bold text-red-800">{PAGES[page].title}</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 min-h-[220px]">{PAGES[page].body}</div>

        <div className="p-5 pt-4 flex-shrink-0">
          <div className="flex items-center justify-center gap-1.5 mb-4">
            {PAGES.map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                aria-label={`Go to page ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === page ? "w-5 bg-red-600" : "w-1.5 bg-gray-300"
                  }`}
              />
            ))}
          </div>

          <div className="flex gap-2">
            {page > 0 && (
              <button
                onClick={() => setPage((p) => p - 1)}
                className="flex items-center justify-center gap-1 px-4 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
            )}
            <button
              onClick={() => (isLast ? handleClose() : setPage((p) => p + 1))}
              className="flex-1 flex items-center justify-center gap-1 bg-red-600 text-white font-bold py-2.5 rounded-xl hover:bg-red-700 transition-colors"
            >
              {isLast ? "Got it!" : "Next"}
              {!isLast && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameRulesModal;
